import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { B } from '../../constants';
import { borderRadius as br, shadows, transitions } from '../../designTokens';

const sizeMap = {
  sm: { p: '6px 12px', fs: 13, is: 15, h: 36, chipFs: 11, chipPy: 2, chipPx: 6 },
  md: { p: '10px 16px', fs: 14, is: 16, h: 44, chipFs: 12, chipPy: 3, chipPx: 8 },
  lg: { p: '14px 20px', fs: 16, is: 18, h: 52, chipFs: 13, chipPy: 4, chipPx: 10 },
};

const radiusMap = {
  xs: br.xs, sm: br.sm, md: br.md, lg: br.lg, xl: br.xl, pill: br.pill,
};

let keyframeInjected = false;
function ensureKeyframes() {
  if (typeof document === 'undefined' || keyframeInjected) return;
  const style = document.createElement('style');
  style.textContent = `@keyframes selectFadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`;
  document.head.appendChild(style);
  keyframeInjected = true;
}

function SelectSpinner({ color = B.muted, size = 14 }) {
  return (
    <span
      style={{
        display: 'inline-flex', width: size, height: size,
        border: `2px solid ${color}33`, borderTopColor: color,
        borderRadius: '50%', animation: 'spin 0.6s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

function SelectChip({ label, onRemove, disabled, size }) {
  const s = sizeMap[size] || sizeMap.md;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: s.chipFs, lineHeight: 1.2,
        padding: `${s.chipPy}px ${s.chipPx}px`,
        background: B.forest + '15', color: B.forest,
        borderRadius: br.xs, whiteSpace: 'nowrap',
        maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {!disabled && (
        <span
          role="button"
          tabIndex={0}
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onRemove?.(); } }}
          aria-label={`Remove ${label}`}
          style={{
            cursor: 'pointer', fontSize: s.chipFs + 2, lineHeight: 1,
            color: B.forest, display: 'inline-flex', flexShrink: 0,
            borderRadius: '50%', width: 14, height: 14,
            alignItems: 'center', justifyContent: 'center',
          }}
        >
          ✕
        </span>
      )}
    </span>
  );
}

function SelectDropdown({
  open, options, filter, multi, value, onChange, renderOption,
  size, maxHeight, highlightedIndex, setHighlightedIndex,
  dropdownRef, listRef, onClose, disabled, id,
  reducedMotion, portal, portalStyle,
}) {
  const s = sizeMap[size] || sizeMap.md;

  useEffect(() => {
    if (open && listRef.current && highlightedIndex >= 0) {
      const item = listRef.current.children[highlightedIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [open, highlightedIndex, listRef]);

  const filtered = filter
    ? options.filter((o) => {
        const term = filter.toLowerCase();
        return (o.label || '').toLowerCase().includes(term) || (o.value || '').toLowerCase().includes(term);
      })
    : options;

  const isSelected = (opt) => {
    if (multi) return Array.isArray(value) && value.includes(opt.value);
    return value === opt.value;
  };

  const handleOptionClick = (opt) => {
    if (opt.disabled) return;
    if (multi) {
      const current = Array.isArray(value) ? [...value] : [];
      const idx = current.indexOf(opt.value);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(opt.value);
      onChange(current);
    } else {
      onChange(opt.value);
      onClose?.();
    }
  };

  if (!open) return null;

  const baseStyle = {
    background: 'white', border: `1.5px solid #e5e0d8`,
    borderRadius: br.md, boxShadow: shadows.lg,
    maxHeight, overflowY: 'auto',
    animation: reducedMotion ? 'none' : 'selectFadeIn 0.15s ease',
  };

  const dropdownStyle = portal
    ? { ...baseStyle, ...portalStyle, position: 'fixed', zIndex: 10000 }
    : { ...baseStyle, position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: 4 };

  return (
    <div ref={dropdownRef} role="listbox" aria-multiselectable={multi || undefined} id={`${id}-listbox`} style={dropdownStyle}>
      {filtered.length === 0 ? (
        <div style={{ padding: s.p, fontSize: s.fs, color: B.muted, textAlign: 'center' }}>
          No options found
        </div>
      ) : (
        filtered.map((opt, idx) => {
          const selected = isSelected(opt);
          const highlighted = idx === highlightedIndex;
          return (
            <div
              key={opt.value}
              id={`${id}-option-${opt.value}`}
              role="option"
              aria-selected={selected}
              aria-disabled={opt.disabled || undefined}
              tabIndex={-1}
              onClick={() => handleOptionClick(opt)}
              onMouseEnter={() => !opt.disabled && setHighlightedIndex(idx)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: s.p, fontSize: s.fs, cursor: opt.disabled ? 'not-allowed' : 'pointer',
                background: highlighted ? B.forest + '0a' : 'transparent',
                color: opt.disabled ? B.muted + '80' : B.night,
                opacity: opt.disabled ? 0.5 : 1,
                transition: reducedMotion ? 'none' : `background ${transitions.fast}`,
              }}
            >
              {multi && (
                <span
                  style={{
                    width: 16, height: 16, flexShrink: 0,
                    border: `2px solid ${selected ? B.forest : '#d0d0d0'}`,
                    borderRadius: 3, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: selected ? B.forest : 'transparent',
                    transition: reducedMotion ? 'none' : `all ${transitions.fast}`,
                  }}
                >
                  {selected && (
                    <span style={{ color: 'white', fontSize: 11, lineHeight: 1 }}>✓</span>
                  )}
                </span>
              )}
              {renderOption ? renderOption(opt, selected, idx) : (
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {opt.label}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

let selectIdCounter = 0;

const Select = forwardRef(({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  variant = 'default',
  size = 'md',
  disabled = false,
  error = false,
  helperText,
  icon,
  rightIcon,
  loading = false,
  searchable = false,
  multi = false,
  clearable = false,
  fullWidth = true,
  radius = 'md',
  focusRingColor = B.forest,
  showFocusRing = true,
  transitionDuration = 'normal',
  style,
  renderOption,
  maxHeight = 250,
  portal = false,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [filter, setFilter] = useState('');
  const [reducedMotion, setReducedMotion] = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const helperIdRef = useRef(null);
  const uniqueIdRef = useRef(null);

  if (!helperIdRef.current) {
    helperIdRef.current = `select-helper-${++selectIdCounter}`;
  }
  if (!uniqueIdRef.current) {
    uniqueIdRef.current = `select-${++selectIdCounter}`;
  }
  const uniqueId = uniqueIdRef.current;

  useEffect(() => {
    ensureKeyframes();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !portal || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPortalStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
    const handleScroll = () => {
      const r = containerRef.current.getBoundingClientRect();
      setPortalStyle({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, portal]);

  const s = sizeMap[size] || sizeMap.md;
  const r = radiusMap[radius] || radiusMap.md;
  const transitionValue = transitions[transitionDuration] || transitionDuration || transitions.normal;
  const isDisabled = disabled || loading;

  const getFilteredOptions = useCallback(() => {
    if (!searchable || !filter) return options;
    const term = filter.toLowerCase();
    return options.filter((o) =>
      (o.label || '').toLowerCase().includes(term) || (o.value || '').toLowerCase().includes(term)
    );
  }, [options, searchable, filter]);

  const hasValue = multi
    ? Array.isArray(value) && value.length > 0
    : value !== undefined && value !== null && value !== '';

  const selectedLabels = multi && Array.isArray(value)
    ? value.map((v) => options.find((o) => o.value === v)).filter(Boolean)
    : [];

  const displayValue = multi
    ? ''
    : options.find((o) => o.value === value)?.label || '';

  const borderColor = error ? B.error : isFocused ? focusRingColor : '#e5e0d8';
  const boxShadow = isFocused && showFocusRing && !error ? `0 0 0 3px ${focusRingColor}25` : 'none';

  const setRef = useCallback((node) => {
    triggerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const openDropdown = useCallback(() => {
    if (isDisabled) return;
    setIsOpen((prev) => !prev);
  }, [isDisabled]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setIsFocused(false);
  }, []);

  const handleTriggerClick = useCallback(() => {
    if (isDisabled) return;
    openDropdown();
  }, [isDisabled, openDropdown]);

  const handleTriggerKeyDown = useCallback((e) => {
    if (isDisabled) return;
    const filtered = getFilteredOptions();

    if (e.key === 'ArrowDown' || (e.key === 'ArrowUp' && isOpen)) {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      setHighlightedIndex((prev) => {
        if (e.key === 'ArrowDown') return Math.min(prev + 1, filtered.length - 1);
        return Math.max(prev - 1, 0);
      });
    } else if (e.key === 'ArrowUp' && !isOpen) {
      e.preventDefault();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        const opt = filtered[highlightedIndex];
        if (opt.disabled) return;
        if (multi) {
          const current = Array.isArray(value) ? [...value] : [];
          const idx = current.indexOf(opt.value);
          if (idx >= 0) current.splice(idx, 1);
          else current.push(opt.value);
          onChange?.(current);
        } else {
          onChange?.(opt.value);
          closeDropdown();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    } else if (e.key === 'Tab') {
      closeDropdown();
    }
  }, [isDisabled, isOpen, highlightedIndex, getFilteredOptions, multi, value, onChange, closeDropdown]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    if (multi) onChange?.([]);
    else onChange?.('');
  }, [onChange, multi]);

  const handleChipRemove = useCallback((removeValue) => {
    if (disabled) return;
    const current = Array.isArray(value) ? [...value] : [];
    const idx = current.indexOf(removeValue);
    if (idx >= 0) current.splice(idx, 1);
    onChange?.(current);
  }, [onChange, value, disabled]);

  const handleSearchInput = useCallback((e) => {
    const v = e.target.value;
    setFilter(v);
    setHighlightedIndex(0);
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    const filtered = getFilteredOptions();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        if (e.key === 'ArrowDown') return Math.min(prev + 1, filtered.length - 1);
        return Math.max(prev - 1, 0);
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
        const opt = filtered[highlightedIndex];
        if (opt.disabled) return;
        if (multi) {
          const current = Array.isArray(value) ? [...value] : [];
          const idx = current.indexOf(opt.value);
          if (idx >= 0) current.splice(idx, 1);
          else current.push(opt.value);
          onChange?.(current);
        } else {
          onChange?.(opt.value);
          closeDropdown();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown();
    }
  }, [getFilteredOptions, highlightedIndex, multi, value, onChange, closeDropdown]);

  const showClear = clearable && hasValue && !isDisabled && !loading;
  const showLoading = loading;
  const showRightIcon = !showLoading && !showClear && !!rightIcon;

  const paddingRight = s.is + 24 + (showLoading || showClear || showRightIcon ? 28 : 0);

  const triggerStyle = {
    width: '100%',
    padding: s.p,
    paddingRight,
    fontSize: s.fs,
    fontFamily: "'Montserrat',sans-serif",
    color: isDisabled ? B.muted : B.night,
    background: isDisabled
      ? '#f5f5f5'
      : error
        ? '#fef2f2'
        : variant === 'filled'
          ? '#f7f4ef'
          : variant === 'outlined'
            ? 'transparent'
            : 'white',
    border: `2px solid ${borderColor}`,
    borderRadius: r,
    outline: 'none',
    boxShadow,
    transition: reducedMotion ? 'none' : `all ${transitionValue}`,
    boxSizing: 'border-box',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: s.h,
    position: 'relative',
    userSelect: 'none',
    ...style,
  };

  const renderTriggerContent = () => {
    if (multi && hasValue) {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1, overflow: 'hidden' }}>
          {selectedLabels.map((opt) => (
            <SelectChip
              key={opt.value}
              label={opt.label}
              size={size}
              disabled={disabled}
              onRemove={() => handleChipRemove(opt.value)}
            />
          ))}
          {searchable && isOpen && (
            <input
              ref={searchInputRef}
              type="text"
              value={filter}
              onChange={handleSearchInput}
              onKeyDown={handleSearchKeyDown}
              onClick={(e) => e.stopPropagation()}
              placeholder=""
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: s.fs, fontFamily: "'Montserrat',sans-serif",
                color: B.night, flex: 1, minWidth: 60,
                padding: 0, margin: 0,
              }}
              aria-label="Search options"
            />
          )}
        </div>
      );
    }

    if (searchable && isOpen) {
      return (
        <input
          ref={searchInputRef}
          type="text"
          value={filter}
          onChange={handleSearchInput}
          onKeyDown={handleSearchKeyDown}
          onClick={(e) => e.stopPropagation()}
          placeholder={hasValue && !multi ? displayValue : placeholder}
          style={{
            border: 'none', outline: 'none', background: 'transparent',
            fontSize: s.fs, fontFamily: "'Montserrat',sans-serif",
            color: B.night, flex: 1, minWidth: 0,
            padding: 0, margin: 0, cursor: isDisabled ? 'not-allowed' : 'text',
          }}
          aria-label="Search options"
        />
      );
    }

    return (
      <span
        style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: hasValue ? B.night : B.muted, textAlign: 'left',
        }}
      >
        {hasValue ? displayValue : placeholder}
      </span>
    );
  };

  const trigger = (
    <div
      ref={setRef}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={`${uniqueId}-listbox`}
      aria-label={props['aria-label']}
      aria-invalid={error || undefined}
      aria-describedby={helperText ? helperIdRef.current : undefined}
      aria-disabled={isDisabled || undefined}
      tabIndex={isDisabled ? -1 : 0}
      onClick={handleTriggerClick}
      onKeyDown={handleTriggerKeyDown}
      onFocus={() => { if (!isDisabled) setIsFocused(true); }}
      onBlur={() => { if (!isOpen) setIsFocused(false); }}
      style={triggerStyle}
    >
      {icon && (
        <span style={{ display: 'inline-flex', flexShrink: 0, fontSize: s.is, color: error ? B.error : B.muted }}>
          {icon}
        </span>
      )}
      {renderTriggerContent()}
      {showLoading ? (
        <SelectSpinner color={B.muted} size={s.is - 2} />
      ) : showClear ? (
        <span
          role="button"
          tabIndex={-1}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleClear}
          aria-label="Clear selection"
          style={{
            cursor: 'pointer', fontSize: s.is, lineHeight: 1,
            color: B.muted, display: 'inline-flex', flexShrink: 0,
          }}
        >
          ✕
        </span>
      ) : showRightIcon ? (
        <span style={{ display: 'inline-flex', flexShrink: 0, fontSize: s.is, color: B.muted, pointerEvents: 'none' }}>
          {rightIcon}
        </span>
      ) : null}
      <span
        style={{
          display: 'inline-flex', flexShrink: 0, fontSize: s.is - 4,
          color: B.muted,
          transition: reducedMotion ? 'none' : `transform ${transitionValue}`,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      >
        ▼
      </span>
    </div>
  );

  const dropdown = (
    <SelectDropdown
      open={isOpen}
      options={options}
      filter={filter}
      multi={multi}
      value={value}
      onChange={onChange}
      renderOption={renderOption}
      size={size}
      maxHeight={maxHeight}
      highlightedIndex={highlightedIndex}
      setHighlightedIndex={setHighlightedIndex}
      dropdownRef={dropdownRef}
      listRef={listRef}
      onClose={closeDropdown}
      disabled={disabled}
      id={uniqueId}
      reducedMotion={reducedMotion}
      portal={portal}
      portalStyle={portalStyle}
    />
  );

  return (
    <div
      ref={containerRef}
      style={{
        width: fullWidth ? '100%' : 'auto',
        display: fullWidth ? 'block' : 'inline-block',
        position: 'relative',
      }}
    >
      {trigger}
      {portal && isOpen ? createPortal(dropdown, document.body) : dropdown}
      {helperText && (
        <div
          id={helperIdRef.current}
          style={{
            marginTop: 6, fontSize: 12,
            fontFamily: "'Montserrat',sans-serif",
            color: error ? B.error : B.muted,
          }}
        >
          {helperText}
        </div>
      )}
    </div>
  );
});

export default Select;
